import {
  validateCardCVC,
  validateCardNumber,
  validateCardExpiry,
} from '../../utilities/payform';
import validatePostalCode from './postcode-validator';
import unpick from '../../../utilities/unpick';
import { CollectError } from '../../../config';

const VALIDATIONS = {
  validCardSecurityCode(value, options) {
    return validateCardCVC(value || '', options.cardType);
  },

  validCardNumber(value) {
    return validateCardNumber(value || '');
  },

  validCardNumberExtended(value) {
    return validateCardNumber(value || '');
  },

  validCardExpirationDate(value) {
    const [month, year] = (value || '').split('/');
    return validateCardExpiry(month, year);
  },

  validPostalCode(value, options, validationName) {
    let countries = getPostalCodeCountry(validationName);
    const validations = countries.map(country => {
      const check = validatePostalCode(value, country);
      if (country.length === 0 ||
        check === `Not valid ${country} country code`) {
        throw new CollectError(check);
      }
      return check;
    });
    return validations.indexOf(true) !== -1;
  },

  validSSN(value) {
    const ssnPattern = /^(?!(000|666|9))(\d{3}-?(?!(00))\d{2}-?(?!(0000))\d{4})$/;
    return ssnPattern.test(value);
  },

  patternValidation(value, options, validationName) {
    let regExpParts, pattern;
    try {
      regExpParts = /\/(.*)\/(.*)/.exec(validationName);
      pattern = new RegExp(regExpParts[1], regExpParts[2]);
    } catch (error) {
      throw new CollectError(`Invalid validation ${validationName}`);
    }
    return pattern.test(value);
  },

  required(value) {
    return value !== undefined && value !== null && value.replace(/^\s+/g, '').replace(/\s+$/g, '').length > 0;
  },
};

const VALIDATION_MESSAGES = {
  required: 'is required',
  validCardExpirationDate: 'is not a valid expiration date',
  validCardNumber: 'is not a valid card number',
  validCardNumberExtended: 'is not a valid card number',
  validCardSecurityCode: 'is not a valid security code',
  patternValidation: 'the value doesn\'t match a pattern',
  validSSN: 'is not a valid ssn',
  validPostalCode(value) {
    return `is not a valid ${getPostalCodeCountry(value)
      .join(',')
      .toUpperCase()} zip/postal code`;
  },
};

function getPostalCodeCountry(value) {
  try {
    return value.split('/')[1].split(',');
  } catch (err) {
    throw new CollectError('Not valid postal code validation format. Specify country.')
  }
}

function runValidation(validationName, value, options, errorMessage) {
  let validationMethod;
  if (validationName.indexOf('postal_code') !== -1) {
    validationMethod = VALIDATIONS.validPostalCode;
  } else {
    validationMethod = VALIDATIONS[validationName] || VALIDATIONS.patternValidation;
  }
  if (validationMethod) {
    if (!validationMethod(value, options, validationName)) {
      return errorMessage;
    }
  } else {
    throw new CollectError(`Invalid validation ${validationName}`);
  }
  return null;
}

export default function validate(value, validations, options) {
  if (validations && validations.validCardNumberExtended && validations.validCardNumber) {
    validations = unpick(validations, ['validCardNumber']);
  }
  return Object.keys(validations || {}).reduce((result, validationName) => {
    let validation, message;
    if (validationName.indexOf('postal_code') !== -1) {
      validation = VALIDATIONS.validPostalCode;
      message = validation.message || VALIDATION_MESSAGES.validPostalCode(validationName);
    } else {
      validation = VALIDATIONS[validationName] || VALIDATIONS.patternValidation;
      message = validation.message || VALIDATION_MESSAGES[validationName] || VALIDATION_MESSAGES.patternValidation;
    }
    const vResult = runValidation(validationName, value, options, message);
    if (vResult !== null) {
      result.push(vResult);
    }
    return result;
  }, []);
}
